import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SpaceService } from '../services/space.service';
import {
  CreateSpaceData,
  SortType,
  Space,
  SpaceComment,
  SpacePoll,
} from '../types/space.types';

interface SpacesState {
  // Spaces list
  spaces: Space[];
  totalSpaces: number;
  currentPage: number;
  hasNextPage: boolean;
  isLoading: boolean;
  error: string | null;
  sortType: SortType;

  // Current space
  currentSpace: Space | null;
  spaceComments: SpaceComment[];
  totalComments: number;
  currentCommentPage: number;
  hasNextCommentPage: boolean;
  isLoadingComments: boolean;
  commentError: string | null;
  loadingComments: Record<string, boolean>; // Track loading state per comment

  // Popular spaces
  popularSpaces: Space[];
  isLoadingPopular: boolean;

  // Popular spaces by interactions
  popularSpacesByInteractions: Space[];
  isLoadingPopularByInteractions: boolean;
  lastPopularSpacesFetchTime: number | null;

  // Actions
  fetchSpaces: (page?: number, limit?: number) => Promise<void>;
  fetchSpace: (spaceId: string) => Promise<void>;
  fetchSpaceComments: (
    spaceId: string,
    page?: number,
    limit?: number
  ) => Promise<void>;
  fetchPopularSpaces: (limit?: number) => Promise<void>;
  fetchPopularSpacesByInteractions: (
    limit?: number,
    forceRefresh?: boolean
  ) => Promise<void>;
  createSpace: (data: CreateSpaceData) => Promise<Space | null>;
  toggleUpvote: (spaceId: string) => Promise<void>;
  toggleFollow: (spaceId: string) => Promise<void>;
  togglePin: (spaceId: string) => Promise<void>;
  toggleCommentUpvote: (
    commentId: string
  ) => Promise<{ success: boolean; isUpvoted: boolean }>;
  addComment: (
    spaceId: string,
    content: string,
    parentId?: string
  ) => Promise<SpaceComment | null>;
  deleteComment: (spaceId: string, commentId: string) => Promise<{ success: boolean }>;
  deleteSpace: (spaceId: string) => Promise<void>;
  setSortType: (sortType: SortType) => void;
  resetState: () => void;
  setSpacePoll: (spaceId: string, poll: SpacePoll) => void;
}

export const useSpacesStore = create<SpacesState>()(
  devtools(
    (set, get) => ({
      // Spaces list
      spaces: [],
      totalSpaces: 0,
      currentPage: 1,
      hasNextPage: false,
      isLoading: false,
      error: null,
      sortType: SortType.NEWEST,

      // Current space
      currentSpace: null,
      spaceComments: [],
      totalComments: 0,
      currentCommentPage: 1,
      hasNextCommentPage: false,
      isLoadingComments: false,
      commentError: null,
      loadingComments: {}, // Track loading state per comment

      // Popular spaces
      popularSpaces: [],
      isLoadingPopular: false,

      // Popular spaces by interactions
      popularSpacesByInteractions: [],
      isLoadingPopularByInteractions: false,
      lastPopularSpacesFetchTime: null,

      // Actions
      fetchSpaces: async (page = 1, limit = 10) => {
        try {
          set({ isLoading: true, error: null });
          const { spaces, total, hasNextPage } = await SpaceService.getSpaces(
            page,
            limit,
            get().sortType
          );

          const pinned = spaces.filter((s) => s.isPinned);
          const unpinned = spaces.filter((s) => !s.isPinned);
          const sortedSpaces = [...pinned, ...unpinned];

          set({
            spaces: sortedSpaces,
            totalSpaces: total,
            currentPage: page,
            hasNextPage,
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Error fetching spaces',
          });
        }
      },

      fetchSpace: async (spaceId: string) => {
        try {
          set({ isLoading: true, error: null });
          const space = await SpaceService.getSpace(spaceId);

          if (space) {
            set({
              currentSpace: space,
              isLoading: false,
            });
          } else {
            set({
              isLoading: false,
              error: 'Space not found',
            });
          }
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Error fetching space',
          });
        }
      },

      fetchSpaceComments: async (spaceId: string, page = 1, limit = 10) => {
        try {
          set({ isLoadingComments: true, commentError: null });
          const { comments, total, hasNextPage } =
            await SpaceService.getSpaceComments(spaceId, page, limit);

          set({
            spaceComments: comments,
            totalComments: total,
            currentCommentPage: page,
            hasNextCommentPage: hasNextPage,
            isLoadingComments: false,
          });
        } catch (error) {
          set({
            isLoadingComments: false,
            commentError:
              error instanceof Error
                ? error.message
                : 'Error fetching comments',
          });
        }
      },

      fetchPopularSpaces: async (limit = 5) => {
        try {
          set({ isLoadingPopular: true });
          const spaces = await SpaceService.getPopularSpaces(limit);

          set({
            popularSpaces: spaces,
            isLoadingPopular: false,
          });
        } catch (error) {
          set({
            isLoadingPopular: false,
            error:
              error instanceof Error
                ? error.message
                : 'Error fetching popular spaces',
          });
        }
      },

      fetchPopularSpacesByInteractions: async (
        limit = 5,
        forceRefresh = false
      ) => {
        try {
          const currentState = get();
          const now = Date.now();
          const staleTime = 5 * 60 * 1000; // 5 minutes stale time

          // Check if we have cached data that's not stale
          if (
            !forceRefresh &&
            currentState.popularSpacesByInteractions.length > 0 &&
            currentState.lastPopularSpacesFetchTime &&
            now - currentState.lastPopularSpacesFetchTime < staleTime
          ) {
            return;
          }

          // If we don't have cached data or it's stale, fetch new data
          set({ isLoadingPopularByInteractions: true });
          const spaces = await SpaceService.getPopularSpacesByInteractions(
            limit
          );

          set({
            popularSpacesByInteractions: spaces,
            isLoadingPopularByInteractions: false,
            lastPopularSpacesFetchTime: Date.now(),
          });
        } catch (error) {
          set({
            isLoadingPopularByInteractions: false,
            error:
              error instanceof Error
                ? error.message
                : 'Error fetching popular spaces by interactions',
          });
        }
      },

      createSpace: async (data: CreateSpaceData) => {
        try {
          set({ isLoading: true, error: null });
          const space = await SpaceService.createSpace(data);

          // Refresh spaces list after creating a new space
          if (space) {
            await get().fetchSpaces(1);
          }

          set({ isLoading: false });
          return space;
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Error creating space',
          });
          return null;
        }
      },

      toggleUpvote: async (spaceId: string) => {
        try {
          const { isUpvoted } = await SpaceService.toggleUpvote(spaceId);

          // Update current space if it's the one being upvoted
          const { currentSpace } = get();
          if (currentSpace && currentSpace.id === spaceId) {
            set({
              currentSpace: {
                ...currentSpace,
                isUpvoted,
                upvotes: isUpvoted
                  ? (currentSpace.upvotes || 0) + 1
                  : Math.max(0, (currentSpace.upvotes || 0) - 1),
              },
            });
          }

          // Update in spaces list
          set((state) => ({
            spaces: state.spaces.map((space) =>
              space.id === spaceId
                ? {
                  ...space,
                  isUpvoted,
                  upvotes: isUpvoted
                    ? (space.upvotes || 0) + 1
                    : Math.max(0, (space.upvotes || 0) - 1),
                }
                : space
            ),
          }));

          // Update in popular spaces
          set((state) => ({
            popularSpaces: state.popularSpaces.map((space) =>
              space.id === spaceId
                ? {
                  ...space,
                  isUpvoted,
                  upvotes: isUpvoted
                    ? (space.upvotes || 0) + 1
                    : Math.max(0, (space.upvotes || 0) - 1),
                }
                : space
            ),
          }));
        } catch (error) {
          set({
            error:
              error instanceof Error ? error.message : 'Error toggling upvote',
          });
        }
      },

      togglePin: async (spaceId: string) => {
        try {
          const { isPinned } = await SpaceService.togglePin(spaceId);

          set((state) => {
            const updatedSpaces = state.spaces.map((space) =>
              space.id === spaceId
                ? { ...space, isPinned, pinnedAt: isPinned ? ({ seconds: Date.now() / 1000 } as any) : null }
                : space
            );

            const pinned = updatedSpaces.filter((s) => s.isPinned);
            const unpinned = updatedSpaces.filter((s) => !s.isPinned);

            return { spaces: [...pinned, ...unpinned] };
          });

          await get().fetchSpaces(get().currentPage);
        } catch (error) {
          console.error('Error toggling pin:', error);
        }
      },

      toggleFollow: async (spaceId: string) => {
        try {
          const { isFollowing } = await SpaceService.toggleFollow(spaceId);

          // Update current space if it's the one being followed
          const { currentSpace } = get();
          if (currentSpace && currentSpace.id === spaceId) {
            set({
              currentSpace: {
                ...currentSpace,
                isFollowing,
              },
            });
          }

          // Update in spaces list
          set((state) => ({
            spaces: state.spaces.map((space) =>
              space.id === spaceId ? { ...space, isFollowing } : space
            ),
          }));

          // Update in popular spaces
          set((state) => ({
            popularSpaces: state.popularSpaces.map((space) =>
              space.id === spaceId ? { ...space, isFollowing } : space
            ),
          }));
        } catch (error) {
          set({
            error:
              error instanceof Error ? error.message : 'Error toggling follow',
          });
        }
      },

      addComment: async (
        spaceId: string,
        content: string,
        parentId?: string
      ) => {
        try {
          // Set loading state only for the specific comment being added
          // instead of the entire comments section
          const loadingKey = parentId || 'root';
          set((state) => ({
            loadingComments: { ...state.loadingComments, [loadingKey]: true },
            commentError: null,
          }));

          const comment = await SpaceService.addComment(
            spaceId,
            content,
            parentId
          );

          if (comment) {
            // If it's a top-level comment, add it to the list
            if (!parentId) {
              set((state) => ({
                spaceComments: [comment, ...state.spaceComments],
                totalComments: state.totalComments + 1,
              }));
            } else {
              // Handle replies with proper nesting
              const findParentAndAddReply = (
                comments: SpaceComment[],
                targetParentId: string,
                newComment: SpaceComment,
                level: number = 0
              ): SpaceComment[] => {
                // Limit to 3 levels of nesting (0, 1, 2)
                const maxNestingLevel = 2;

                return comments.map((c) => {
                  // Found direct parent
                  if (c.id === targetParentId) {
                    // If we're already at max nesting level, add to parent's parent instead
                    if (level >= maxNestingLevel) {
                      return {
                        ...c,
                        replies: [
                          ...(c.replies || []),
                          { ...newComment, parentId: c.id },
                        ],
                      };
                    } else {
                      return {
                        ...c,
                        replies: [...(c.replies || []), newComment],
                      };
                    }
                  }

                  // Check if parent is in replies
                  if (c.replies && c.replies.length > 0) {
                    return {
                      ...c,
                      replies: findParentAndAddReply(
                        c.replies,
                        targetParentId,
                        newComment,
                        level + 1
                      ),
                    };
                  }

                  return c;
                });
              };

              // Find the parent comment at any level and add the reply
              set((state) => ({
                spaceComments: findParentAndAddReply(
                  state.spaceComments,
                  parentId,
                  comment
                ),
                totalComments: state.totalComments + 1,
              }));
            }

            // Update comment count in the current space
            const { currentSpace } = get();
            if (currentSpace && currentSpace.id === spaceId) {
              set({
                currentSpace: {
                  ...currentSpace,
                  comments: (currentSpace.comments || 0) + 1,
                  commentCount: (currentSpace.commentCount || 0) + 1,
                },
              });
            }
          }

          // Clear loading state for this specific comment
          set((state) => {
            const { [loadingKey]: _, ...restLoadingComments } =
              state.loadingComments;
            return { loadingComments: restLoadingComments };
          });

          return comment;
        } catch (error) {
          // Clear loading state on error
          const loadingKey = parentId || 'root';
          set((state) => {
            const { [loadingKey]: _, ...restLoadingComments } =
              state.loadingComments;
            return {
              loadingComments: restLoadingComments,
              commentError:
                error instanceof Error ? error.message : 'Error adding comment',
            };
          });

          return null;
        }
      },

      deleteComment: async (spaceId: string, commentId: string) => {
        try {
          set({ isLoadingComments: true, commentError: null });
          const result = await SpaceService.deleteComment(spaceId, commentId);

          if (result.success) {
            // Remove the comment from the state
            const { spaceComments, currentSpace } = get();

            // Find the comment to remove
            const commentToRemove = spaceComments.find((c) => c.id === commentId);

            if (commentToRemove) {
              // If it's a top-level comment, remove it directly
              set((state) => ({
                spaceComments: state.spaceComments.filter(
                  (c) => c.id !== commentId
                ),
                totalComments: state.totalComments - 1,
              }));

              // Update comment count in the current space
              if (currentSpace) {
                set({
                  currentSpace: {
                    ...currentSpace,
                    comments: Math.max(0, (currentSpace.comments || 0) - 1),
                    commentCount: Math.max(
                      0,
                      (currentSpace.commentCount || 0) - 1
                    ),
                  },
                });
              }
            } else {
              // It might be a reply, find its parent
              let parentCommentId;
              let replyIndex = -1;

              for (const comment of spaceComments) {
                if (comment.replies) {
                  const index = comment.replies.findIndex(
                    (r) => r.id === commentId
                  );
                  if (index !== -1) {
                    parentCommentId = comment.id;
                    replyIndex = index;
                    break;
                  }
                }
              }

              if (parentCommentId !== undefined && replyIndex !== -1) {
                set((state) => ({
                  spaceComments: state.spaceComments.map((c) => {
                    if (c.id === parentCommentId) {
                      return {
                        ...c,
                        replies: c.replies?.filter((_, i) => i !== replyIndex),
                      };
                    }
                    return c;
                  }),
                  totalComments: state.totalComments - 1,
                }));

                // Update comment count in the current space
                if (currentSpace) {
                  set({
                    currentSpace: {
                      ...currentSpace,
                      comments: Math.max(0, (currentSpace.comments || 0) - 1),
                      commentCount: Math.max(
                        0,
                        (currentSpace.commentCount || 0) - 1
                      ),
                    },
                  });
                }
              }
            }
          }

          set({ isLoadingComments: false });
          return result;
        } catch (error) {
          set({
            isLoadingComments: false,
            commentError:
              error instanceof Error ? error.message : 'Error deleting comment',
          });
          return { success: false };
        }
      },

      toggleCommentUpvote: async (commentId: string) => {
        try {
          // Call the API to toggle the upvote
          const response = await SpaceService.toggleCommentUpvote(commentId);

          // Update the comment in the state
          set((state) => ({
            spaceComments: state.spaceComments.map((comment) => {
              if (comment.id === commentId) {
                return {
                  ...comment,
                  upvotes: response.isUpvoted
                    ? (comment.upvotes || 0) + 1
                    : Math.max(0, (comment.upvotes || 0) - 1),
                  isUpvoted: response.isUpvoted,
                };
              }
              return comment;
            }),
          }));

          return { success: true, isUpvoted: response.isUpvoted };
        } catch (error) {
          console.error('Error toggling comment upvote:', error);
          return { success: false, isUpvoted: false };
        }
      },

      deleteSpace: async (spaceId: string) => {
        try {
          set({ isLoading: true, error: null });
          await SpaceService.deleteSpace(spaceId);

          // Remove the deleted space from the list
          set((state) => ({
            spaces: state.spaces.filter((space) => space.id !== spaceId),
            isLoading: false,
          }));

          // If the current space is the deleted one, reset it
          const { currentSpace } = get();
          if (currentSpace && currentSpace.id === spaceId) {
            set({ currentSpace: null });
          }
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
          throw error;
        }
      },

      // Voting itself is done by PollDisplay, which hands the fresh poll over.
      setSpacePoll: (spaceId: string, poll: SpacePoll) => {
        set((state) => ({
          currentSpace:
            state.currentSpace?.id === spaceId
              ? { ...state.currentSpace, poll }
              : state.currentSpace,
          spaces: state.spaces.map((space) =>
            space.id === spaceId ? { ...space, poll } : space
          ),
        }));
      },

      setSortType: (sortType: SortType) => {
        set({ sortType });
        // Fetch spaces with the new sort type
        get().fetchSpaces(1);
      },

      resetState: () => {
        set({
          spaces: [],
          totalSpaces: 0,
          currentPage: 1,
          hasNextPage: false,
          currentSpace: null,
          spaceComments: [],
          totalComments: 0,
          currentCommentPage: 1,
          hasNextCommentPage: false,
          error: null,
          commentError: null,
        });
      },
    }),
    { name: 'spaces-store' }
  )
);
